import { ANALYTICS_EVENTS } from '@nandosaus/constants';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';

import { P } from '@components/typography/new';
import { analytics } from '../../analytics';
import { breakpoints } from '../../constants';
import { Container, Col, Box, Flex, Row } from '../grid';
import { Icon } from '../icons';
import { pStyles } from '../typography/new/body';

const SearchBar = ({ onSearchChange, onClose, showSearch, searchValue, id, ...rest }) => {
  return (
    <StyledContainer showSearch={showSearch} id={id}>
      <Row>
        <StyledSearchCol display={showSearch ? 'flex' : 'none'} showSearch={showSearch} {...rest}>
          <TextField>
            <Box mr="4px" display="flex" alignItems="center">
              <Icon name="search" fill="greyPrimary" height={{ _: '20px', lg: '24px' }} />
            </Box>
            <TextInput
              name="searchField"
              placeholder="Search our menu e.g. burger, salad..."
              value={searchValue}
              onChange={event => {
                const { value } = event.target;
                onSearchChange(value);
              }}
              aria-label="clear search results"
              autoFocus
            />
            {searchValue && (
              <Box
                as="button"
                onClick={() => {
                  onSearchChange('');
                  analytics.track(ANALYTICS_EVENTS.MENU_SEARCH_CLEARED);
                }}
              >
                <P textDecoration="underline" variant="text-link">
                  clear
                </P>
              </Box>
            )}
          </TextField>
          <HideSearchButton
            ml="12px"
            height="36px"
            width="36px"
            minWidth="36px"
            justifyContent="center"
            alignItems="center"
            borderRadius="100%"
            display="flex"
            onClick={() => {
              onClose();
              onSearchChange('');
              analytics.track(ANALYTICS_EVENTS.MENU_SEARCH_CLEARED);
            }}
            aria-label="Hide search field"
          >
            <Icon name="cross" height={{ _: '20px', lg: '24px' }} />
          </HideSearchButton>
        </StyledSearchCol>
      </Row>
    </StyledContainer>
  );
};

const StyledContainer = styled(Container)`
  @media (max-width: ${breakpoints.lg}) {
    ${({ showSearch }) =>
      showSearch &&
      `
      max-width: none;
      padding: 0;
    `}
  }
`;

const slideDown = keyframes`
    from {
      opacity: 0;
      transform: translateY(-100px);
    }
    to {
      opacity: 1;
      transform: translateY(0px);
    }
`;

const SearchColStyles = css`
  align-items: center;
  padding-top: 24px;
  margin-left: 0.5rem;
  margin-right: 0.5rem;

  @media (min-width: ${breakpoints.md}) {
    top: 120px;
  }

  @media (min-width: ${breakpoints.lg}) {
    background-color: transparent;
    justify-content: flex-start;
    position: relative;
    top: 0px;
  }
`;

const StyledSearchCol = styled(Col)`
  ${SearchColStyles}

  animation: ${slideDown} ease 0.4s forwards;
  background-color: ${({ theme }) => theme.colors.warmWhite2};
  position: sticky;
  top: 100px;
  transform-origin: top;
  transition: transform 0.2s ease-in;
  wrap: nowrap;
  z-index: 19;

  padding-left: 1.5rem;
  padding-right:  1.5rem;

  @media (min-width: ${breakpoints.md}) {
    padding-left: 2rem;
    padding-right: 2rem;
  }

  @media (min-width: ${breakpoints.lg}) {
    border-bottom: none;
    transform: translateY(0);
    padding-left: 0;
    padding-right: 0;
  }
`;

const ShowHideSearchButton = css`
  border: none;
  background-color: transparent;

  &:hover {
    outline: 1px solid black;
  }

  div {
    text-transform: none;
  }
`;

const HideSearchButton = styled(Box)`
  ${ShowHideSearchButton}
`;

const TextField = styled(Flex)`
  align-items: center;
  background-color: transparent;
  padding: 12px 11px;

  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grey70};
  width: 100%;
`;

const TextInput = styled.input`
  ${pStyles('small')}
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.black};
  width: 100%;

  ::placeholder {
    color: ${({ theme }) => theme.colors.grey50};
  }
`;

SearchBar.propTypes = {
  onSearchChange: PropTypes.func.isRequired,
  searchValue: PropTypes.string.isRequired,
  showSearch: PropTypes.bool,
  onClose: PropTypes.func,
  id: PropTypes.string,
};

SearchBar.defaultProps = {
  showSearch: false,
  onClose: () => {},
  id: undefined,
};

export { SearchBar };
